/*
  molecules.js — Edible Growth Shopify Theme
  Port of InteractiveMoleculeChart.jsx + AtomNode.jsx to vanilla JS.

  React approach: useTime() → useTransform() → continuous radian rotation
    fed into each atom's translateX/Y via motion values.

  Vanilla approach: requestAnimationFrame loop computing the same trig,
    updating atom positions and SVG ring rotation directly via style.
    Visually identical output at the same ~20s-per-rotation cadence.

  Cannabinoid data is embedded here (mirrors cannabinoidData.js).
  The molecule chart gets rendered into [data-molecule-chart] containers.
*/

(function (global) {
  'use strict';

  /* ── CANNABINOID DATA (port of cannabinoidData.js) ─────────── */
  var cannabinoidDetails = {
    'THC':           { name:'THC',       color:'#fca5a5', description:'Tetrahydrocannabinol (THC) is the principal psychoactive constituent of cannabinoids. In low doses, it can aid in pain relief, relaxation, and mood enhancement.', wiifm:'At this micro-dose level (<0.3%), THC helps to relax both mind and body and enhances the overall effectiveness of other cannabinoids through the \'entourage effect,\' without a noticeable high.' },
    'THCA':          { name:'THCA',      color:'#fecaca', description:'Tetrahydrocannabinolic acid (THCA) is the non-psychoactive precursor to THC found in raw cannabinoids. It is being studied for its anti-inflammatory and neuroprotective properties.', wiifm:'Think of this as raw fuel. While not \'active\' itself, it\'s the foundation from which THC\'s benefits are built, contributing to the plant\'s overall therapeutic potential.' },
    'THCV':          { name:'THCV',      color:'#f97316', description:'Tetrahydrocannabivarin (THCV) is a cannabinoid that may produce different effects than THC, including potential appetite suppression and blood sugar regulation.', wiifm:'Known as \'diet weed,\' this cannabinoid helps curb the munchies and may give a more energetic, clear-headed feeling compared to traditional THC.' },
    'Delta-8-THC':   { name:'Δ8-THC',   color:'#fb923c', description:'Delta-8-THC is an analog of THC with a similar chemical structure but some notable differences. It is generally considered to be less psychoactive than Delta-9-THC.', wiifm:'This offers a milder, more clear-headed high than standard THC, providing relaxation and mood elevation with less anxiety or paranoia.' },
    'Delta-10-THC':  { name:'Δ10-THC',  color:'#fdba74', description:'Delta-10-THC is another isomer of THC, similar to Delta-8. It is known for producing a milder, more energizing and sativa-like effect.', wiifm:'If Delta-8 is for relaxing, Delta-10 is for getting things done. It provides a creative, uplifting buzz that\'s perfect for daytime use.' },
    'THCP':          { name:'THCP',      color:'#ef4444', description:'Tetrahydrocannabiphorol (THCP) is a recently discovered cannabinoid believed to be significantly more potent than THC due to its longer alkyl side chain.', wiifm:'This is the \'super-THC.\' It\'s much stronger and can produce powerful effects even in small amounts.' },
    '11-hydroxy-THC':{ name:'11-OH-THC', color:'#f87171', description:'11-hydroxy-THC is the primary active metabolite of THC formed in the body after ingestion, particularly via edibles. It is more potent and has a longer-lasting effect.', wiifm:'This is what gives edibles their powerful, long-lasting kick. Your body converts THC into this more potent form that crosses the blood-brain barrier with ease.' },
    'CBN':  { name:'CBN',  color:'#c4b5fd', description:'Cannabinol (CBN) is a mildly psychoactive cannabinoid that forms when THC ages. It is primarily researched for its sedative properties and potential as a sleep aid.', wiifm:'This is your \'off-switch.\' It promotes a powerful sense of calm and sedation, helping you fall asleep faster and stay asleep longer.' },
    'CBD':  { name:'CBD',  color:'#6ee7b7', description:'Cannabidiol (CBD) is a non-psychoactive compound praised for its anti-inflammatory, anti-anxiety, and neuroprotective properties without inducing a \'high\'.', wiifm:'It helps calm your central nervous system, reducing jitters and anxiety while fighting inflammation, allowing you to stay focused, steady, and in control.' },
    'CBDA': { name:'CBDA', color:'#a7f3d0', description:'Cannabidiolic acid (CBDA) is the raw, acidic form of CBD. It is believed to have a stronger affinity for certain serotonin receptors, suggesting powerful anti-nausea and anti-anxiety effects.', wiifm:'The raw power behind CBD. It works directly on serotonin receptors, making it extra effective for settling your stomach and calming your mind.' },
    'CBDV': { name:'CBDV', color:'#059669', description:'Cannabidivarin (CBDV) is a homolog of CBD. It is being researched for its potential in treating neurological conditions such as epilepsy.', wiifm:'A specialist cannabinoid that shows promise in calming neurological activity.' },
    'CBG':  { name:'CBG',  color:'#93c5fd', description:'Cannabigerol (CBG), often called the \'mother cannabinoid,\' is a non-psychoactive compound from which many other cannabinoids are synthesized. Known for its potential to fight inflammation, pain, and nausea.', wiifm:'Think of this as CBD\'s more potent cousin for physical recovery. It targets inflammation directly at the source, helping to soothe sore muscles and joints.' },
    'CBGA': { name:'CBGA', color:'#bfdbfe', description:'Cannabigerolic acid (CBGA) is the foundational compound for major cannabinoids — the precursor from which the plant creates THCA, CBDA, and CBCA.', wiifm:'This is the \'stem cell\' of cannabinoids. All the major players start as CBGA before they become the compounds you know.' },
    'CBC':  { name:'CBC',  color:'#38bdf8', description:'Cannabichromene (CBC) is a non-psychoactive cannabinoid being studied for its anti-inflammatory, antidepressant, and brain-growth-promoting properties.', wiifm:'This is the ultimate team player. It boosts the effects of other cannabinoids and has its own unique mood-lifting and brain-supporting benefits.' },
    'CBCA': { name:'CBCA', color:'#7dd3fc', description:'Cannabichromenic acid (CBCA) is the acidic precursor to CBC. It contributes to the entourage effect and is being studied for its anti-inflammatory properties.', wiifm:'The raw, unactivated form of CBC. It\'s a key part of the plant\'s natural defense system.' },
  };

  var thcFamilyIds = ['THC','THCA','THCV','Delta-8-THC','Delta-10-THC','THCP','11-hydroxy-THC'];
  var cbdFamilyIds = ['CBD','CBDA','CBDV','CBG','CBGA','CBC','CBCA','CBN'];

  /* ── CHART RENDERER ───────────────────────────────────────── */
  var DURATION  = 20000; /* ms per full rotation — matches Framer 20s */
  var RADIUS    = 120;   /* px — matches InteractiveMoleculeChart */
  var ATOM_SIZE = 56;    /* px — matches AtomNode w-14 h-14 */
  var CHART_SIZE = 300;  /* px — matches InteractiveMoleculeChart 300px */

  function buildChart(container, centerId, surroundingIds, reverse) {
    var centerAtom = cannabinoidDetails[centerId];
    var outerAtoms = surroundingIds
      .map(function (id) { return cannabinoidDetails[id]; })
      .filter(Boolean);

    if (!centerAtom) return;

    container.style.position = 'relative';
    container.style.width    = CHART_SIZE + 'px';
    container.style.height   = CHART_SIZE + 'px';
    container.style.display  = 'flex';
    container.style.alignItems   = 'center';
    container.style.justifyContent = 'center';

    /* ── SVG connector lines ─────────────────────────────── */
    var ns  = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 ' + CHART_SIZE + ' ' + CHART_SIZE);
    svg.style.cssText = 'position:absolute;width:100%;height:100%;top:0;left:0;transform-origin:center center';

    var defs  = document.createElementNS(ns, 'defs');
    var gradId = 'mol-grad-' + centerId.replace(/[^a-z0-9]/gi, '_');
    var grad  = document.createElementNS(ns, 'linearGradient');
    grad.id   = gradId;
    grad.setAttribute('x1','0%'); grad.setAttribute('y1','0%');
    grad.setAttribute('x2','100%'); grad.setAttribute('y2','100%');
    var s1 = document.createElementNS(ns, 'stop');
    s1.setAttribute('offset','0%');
    s1.setAttribute('stop-color', centerAtom.color);
    s1.setAttribute('stop-opacity','0.3');
    var s2 = document.createElementNS(ns, 'stop');
    s2.setAttribute('offset','100%');
    s2.setAttribute('stop-color','#ffffff');
    s2.setAttribute('stop-opacity','0.3');
    grad.appendChild(s1); grad.appendChild(s2);
    defs.appendChild(grad); svg.appendChild(defs);

    var cx = CHART_SIZE / 2;
    var cy = CHART_SIZE / 2;

    outerAtoms.forEach(function (_, i) {
      var angle = (i / outerAtoms.length) * 2 * Math.PI;
      var line  = document.createElementNS(ns, 'line');
      line.setAttribute('x1', cx);
      line.setAttribute('y1', cy);
      line.setAttribute('x2', cx + RADIUS * Math.cos(angle));
      line.setAttribute('y2', cy + RADIUS * Math.sin(angle));
      line.setAttribute('stroke', 'url(#' + gradId + ')');
      line.setAttribute('stroke-width', '1');
      svg.appendChild(line);
    });

    container.appendChild(svg);

    /* ── Outer atom buttons ──────────────────────────────── */
    var atomEls = outerAtoms.map(function (atom, i) {
      var btn = document.createElement('button');
      btn.className   = 'eg-atom-node';
      btn.textContent = atom.name;
      btn.style.cssText = [
        'position:absolute',
        'width:'  + ATOM_SIZE + 'px',
        'height:' + ATOM_SIZE + 'px',
        'border-radius:50%',
        'background:' + atom.color,
        'box-shadow:0 0 15px ' + atom.color,
        'color:#fff',
        'font-weight:700',
        'font-size:0.7rem',
        'border:none',
        'cursor:pointer',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'text-align:center',
        'line-height:1.2',
        'padding:0.25rem',
        'transition:transform 0.2s ease',
        'transform-origin:center center',
        'z-index:2',
      ].join(';');

      btn.addEventListener('mouseenter', function () { btn.style.transform = 'scale(1.2)'; });
      btn.addEventListener('mouseleave', function () { btn.style.transform = 'scale(1)'; });

      btn.addEventListener('click', function () {
        if (global.EGModals) {
          EGModals.open('eg-modal-molecule', atom, 'molecule');
        }
      });

      container.appendChild(btn);
      return { el: btn, baseAngle: (i / outerAtoms.length) * 2 * Math.PI };
    });

    /* ── Center atom button ──────────────────────────────── */
    var centerBtn = document.createElement('button');
    centerBtn.className   = 'eg-atom-center';
    centerBtn.textContent = centerAtom.name;
    centerBtn.style.cssText = [
      'position:relative',
      'z-index:3',
      'width:6rem',
      'height:6rem',
      'border-radius:50%',
      'background:' + centerAtom.color,
      'box-shadow:0 0 30px ' + centerAtom.color,
      'color:#fff',
      'font-weight:700',
      'font-size:1.25rem',
      'border:none',
      'cursor:pointer',
      'transition:transform 0.2s ease',
    ].join(';');

    centerBtn.addEventListener('mouseenter', function () { centerBtn.style.transform = 'scale(1.1)'; });
    centerBtn.addEventListener('mouseleave', function () { centerBtn.style.transform = 'scale(1)'; });

    centerBtn.addEventListener('click', function () {
      if (global.EGModals) {
        EGModals.open('eg-modal-molecule', centerAtom, 'molecule');
      }
    });

    container.appendChild(centerBtn);

    /* ── RAF animation loop ──────────────────────────────── */
    var startTime = null;
    var dir = reverse ? -1 : 1;

    function frame(ts) {
      if (!startTime) startTime = ts;
      var elapsed  = ts - startTime;
      var rotation = dir * (elapsed / DURATION) * 2 * Math.PI;

      /* Rotate the SVG connector lines */
      svg.style.transform = 'rotate(' + (rotation * 180 / Math.PI) + 'deg)';

      /* Move each outer atom along the orbit */
      atomEls.forEach(function (a) {
        var angle = a.baseAngle + rotation;
        var x = cx + RADIUS * Math.cos(angle) - ATOM_SIZE / 2;
        var y = cy + RADIUS * Math.sin(angle) - ATOM_SIZE / 2;
        a.el.style.left = x + 'px';
        a.el.style.top  = y + 'px';
      });

      requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  /* ── INIT ALL CHARTS ─────────────────────────────────────── */
  function init() {
    document.querySelectorAll('[data-molecule-chart]').forEach(function (container) {
      var centerId       = container.dataset.moleculeCenter;
      var surroundingRaw = container.dataset.moleculeSurrounding;
      var reverse        = container.dataset.moleculeReverse === 'true';

      if (!centerId || !surroundingRaw) return;

      var surroundingIds = surroundingRaw.split(',').map(function (s) { return s.trim(); });
      buildChart(container, centerId, surroundingIds, reverse);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Expose data for other scripts if needed */
  global.EGMolecules = { cannabinoidDetails: cannabinoidDetails };

})(window);
